import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  approveOpenShiftRequest,
  fetchAccountOpenShiftRequests,
  OPEN_SHIFT_REQUESTS_EMPTY,
  OPEN_SHIFT_REQUESTS_PAGE_LIMIT,
  DECLINE_RECEIPT,
  DECLINE_REASON_REQUIRED,
  hasLegibleDeclineReason,
  rejectOpenShiftRequest,
  REJECTION_REASON_CONSOLE_MAX,
  REJECTION_REASON_MAX,
  type OpenShiftRequest,
  type OpenShiftRequestsLoad,
} from "../data/open-shift-requests";
import { useReadModel } from "../data/useReadModel";
import { TemplateColorChip } from "../components/TemplateColorChip";
import { formatDisplayTime } from "../lib/display-time";

// Open-shift request inbox (Wave 6B). Pending asks from guards against `request_approval`
// templates, with approve and reject-with-reason.
//
// Three honesty rules shape this surface. (1) "Nothing pending" and "this server has no
// request workflow" are separate states with separate copy - an empty inbox must never be
// shown for a missing RPC. (2) Approve is server-authoritative: losing the race renders the
// contract's own sentence and refetches, because that request is now unactionable. (3) The
// badge is the length of one page (Wave 6A ships no count RPC), so it reads "50+" at the
// limit rather than inventing a total.
//
// Refresh is polling only. Worker push for "open shift available" is explicitly out of 6A,
// so there is no realtime subscription and no invented notification RPC here.

export function openShiftRequestBadge(load: OpenShiftRequestsLoad | null): string | null {
  if (load === null || load.status !== "ready" || load.requests.length === 0) return null;
  return load.requests.length >= OPEN_SHIFT_REQUESTS_PAGE_LIMIT
    ? `${OPEN_SHIFT_REQUESTS_PAGE_LIMIT}+`
    : String(load.requests.length);
}

/** "12 min ago" / "3 h ago" - a supervisor triages by age, not by timestamp. */
export function relativeAge(iso: string | null, now: number): string {
  if (iso === null) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const minutes = Math.max(0, Math.round((now - then) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function slotWindow(request: OpenShiftRequest, timeZone: string): string {
  const start = request.slotStartsAt ? formatDisplayTime(request.slotStartsAt, timeZone) : "-";
  const end = request.slotEndsAt ? formatDisplayTime(request.slotEndsAt, timeZone) : "-";
  return `${start} to ${end}`;
}

function placeLabel(request: OpenShiftRequest): string {
  // `site_name` arrives as '' only when truly unreachable; the mapper turned that into null,
  // so the row renders the route alone rather than leaving a gap.
  if (request.siteName !== null && request.routeName !== null) {
    return `${request.siteName} - ${request.routeName}`;
  }
  return request.siteName ?? request.routeName ?? "Route unknown";
}

export function OpenShiftRequestInbox({
  client,
  accountId,
  timeZone,
  canManage = false,
  pollMs = 30000,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  /** schedule.manage - affordances only exist when true (fail closed); the server re-checks. */
  canManage?: boolean;
  pollMs?: number;
}) {
  const { status, data, error, reload } = useReadModel<OpenShiftRequestsLoad>(
    () => fetchAccountOpenShiftRequests(client, accountId),
    [client, accountId],
    pollMs,
  );

  if (status === "loading" && data === null) {
    return (
      <p className="surface__state" role="status">
        Loading open shift requests...
      </p>
    );
  }
  if (status === "error" || data === null) {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load open shift requests."}
      </p>
    );
  }
  if (data.status === "unavailable") {
    return (
      <p className="surface__state" role="status">
        {data.reason}
      </p>
    );
  }
  if (data.status === "failed") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {data.reason}
      </p>
    );
  }
  if (data.requests.length === 0) {
    return (
      <p className="surface__state" role="status">
        {OPEN_SHIFT_REQUESTS_EMPTY}
      </p>
    );
  }

  const badge = openShiftRequestBadge(data);
  return (
    <div className="osr">
      <p className="schedule__note" role="status">
        {badge} pending request{data.requests.length === 1 ? "" : "s"}
        {canManage ? "." : " - review needs schedule manage permission."}
      </p>
      <ul className="osr__list" aria-label="Open shift requests">
        {data.requests.map((request) => (
          <OpenShiftRequestRow
            key={request.requestId}
            client={client}
            accountId={accountId}
            request={request}
            timeZone={timeZone}
            canManage={canManage}
            onReviewed={reload}
          />
        ))}
      </ul>
    </div>
  );
}

function OpenShiftRequestRow({
  client,
  accountId,
  request,
  timeZone,
  canManage,
  onReviewed,
}: {
  client: SupabaseClient;
  accountId: string;
  request: OpenShiftRequest;
  timeZone: string;
  canManage: boolean;
  onReviewed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<{ text: string; tone: "approved" | "declined" } | null>(
    null,
  );
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  // Fail closed: whitespace is not a reason, so the primary control stays off until there
  // is something an officer could actually read. Same predicate the write uses, so a button
  // that looks armed can never be one the data layer would refuse.
  const hasReason = hasLegibleDeclineReason(reason);
  const readerName = request.requesterDisplayName ?? "The officer";
  const counterId = `osr-count-${request.requestId}`;
  const failClosedId = `osr-failclosed-${request.requestId}`;

  async function review(
    run: () => Promise<Awaited<ReturnType<typeof approveOpenShiftRequest>>>,
    receiptOnOk: { text: string; tone: "approved" | "declined" },
  ) {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    const outcome = await run();
    setBusy(false);
    if (outcome.ok) {
      // Resolve in place: the list does not reshuffle under the cursor mid-review.
      // The receipt names the action the supervisor took, never a guess read back out of the
      // response: `open_shift_request_result` inner-joins site and route, so a successful
      // write can still return no mappable row, and inferring from that would have announced
      // a decline - "the seat stays open" - over an approval that just filled the seat.
      setReceipt(receiptOnOk);
      setRejecting(false);
      onReviewed();
      return;
    }
    setFailure(outcome.reason);
    if (outcome.stale) {
      // This request is no longer actionable; show why, then refetch so the board is honest.
      onReviewed();
    }
  }

  return (
    <li className={`osr__row tpl-color--${request.colorToken}`}>
      <div className="osr__head">
        <TemplateColorChip colorToken={request.colorToken} />
        <span className="osr__who">{request.requesterDisplayName ?? "Member"}</span>
        <span className="osr__meta">{relativeAge(request.createdAt, Date.now())}</span>
      </div>
      <p className="osr__meta">
        {placeLabel(request)} · {slotWindow(request, timeZone)}
      </p>
      {request.requestNote !== null ? <p className="osr__note">{request.requestNote}</p> : null}
      {receipt !== null ? (
        // Proof green is reserved for verified success. A decline is a correct outcome but
        // not a success, so it reads warn-amber; approve keeps the green.
        <p
          className={receipt.tone === "approved" ? "builder__drawer-ok" : "osr__declined"}
          role="status"
        >
          {receipt.text}
        </p>
      ) : canManage ? (
        <>
          <div className="osr__actions">
            <button
              type="button"
              className="minter__confirm-go"
              disabled={busy}
              onClick={() =>
                void review(
                  () => approveOpenShiftRequest(client, accountId, request.requestId),
                  { text: "Approved - the assignment is created.", tone: "approved" },
                )
              }
            >
              Approve
            </button>
            <button
              type="button"
              className="builder__navbtn"
              disabled={busy}
              aria-expanded={rejecting}
              onClick={() => setRejecting((open) => !open)}
            >
              Decline…
            </button>
          </div>
          {rejecting ? (
            <div className="osr__reject">
              <label className="tpl__field osr__reason">
                <span className="osr__reason-label">
                  Reason the officer will see <span className="osr__req">· required</span>
                </span>
                <span className="osr__reason-help">
                  {readerName} reads exactly this in the Guard app. Write it to them: what
                  happened, and what to do instead.
                </span>
                {/* Capped at the console's editorial limit, not the contract's 500. The data
                    layer re-checks both the emptiness and the length, so a programmatic
                    caller cannot get past either. */}
                <textarea
                  rows={3}
                  value={reason}
                  maxLength={REJECTION_REASON_CONSOLE_MAX}
                  aria-describedby={`${failClosedId} ${counterId}`}
                  placeholder="e.g. Saturday went to an earlier request. Friday 22:00 at North Yard is still open if you want it."
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              <div className="osr__reason-meta">
                <span className="osr__counter" id={counterId}>
                  {reason.trim().length} / {REJECTION_REASON_CONSOLE_MAX}
                </span>
                <span className="osr__reason-contract">
                  Server contract allows up to {REJECTION_REASON_MAX} characters. The console
                  caps shorter on purpose.
                </span>
              </div>
              <div className="osr__actions">
                <button
                  type="button"
                  className="builder__navbtn osr__decline"
                  disabled={busy || !hasReason}
                  onClick={() =>
                    void review(
                      () => rejectOpenShiftRequest(client, accountId, request.requestId, reason),
                      // Storage status stays `rejected` (frozen enum); the desk reads
                      // "Declined". The seat claim is the migration's: reject only flips the
                      // request row and writes an audit event - it creates no assignment and
                      // touches no slot, so nothing about it consumes the seat.
                      // Says only what is verifiable from this side. The reason is stored
                      // against the request; whether the officer has SEEN it depends on the
                      // guard surface rendering declined rows, which is a separate lane. A
                      // receipt that claimed delivery would be the same silent-drop problem
                      // this change exists to fix, just moved to the desk.
                      { text: DECLINE_RECEIPT, tone: "declined" },
                    )
                  }
                >
                  Decline and notify
                </button>
                <button
                  type="button"
                  className="builder__navbtn"
                  disabled={busy}
                  onClick={() => {
                    setRejecting(false);
                    setReason("");
                    setFailure(null);
                  }}
                >
                  Keep pending
                </button>
              </div>
              <p className="osr__failclosed" id={failClosedId}>
                {DECLINE_REASON_REQUIRED}
              </p>
            </div>
          ) : null}
        </>
      ) : null}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
    </li>
  );
}
